/** "Som neste aparelho" na tela de preferências (parte D1; desenho §5.3). O mesmo valor do
 *  interruptor do sino (`useSomDoAparelho`). "Ouvir" toca mesmo com o som desligado: é um
 *  pedido explícito, dentro do clique. Sem Web Audio, o cartão não aparece. */
import { useEffect, useId, useRef, useState } from "react";
import { Play, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useSomDoAparelho } from "@/hooks/useSomDoAparelho";
import { audioDisponivel, tocarSomDeAviso, type TipoDeSom } from "@/lib/somDeAviso";

export function CartaoSomDoAparelho({ className }: { className?: string } = {}) {
  const [ligado, definir] = useSomDoAparelho();
  const id = useId();
  const [tocando, setTocando] = useState<TipoDeSom | null>(null);
  const timerDoOuvir = useRef<number | undefined>(undefined);

  // Saiu da tela no meio do "Ouvir": o temporizador não sobrevive ao cartão.
  useEffect(() => () => window.clearTimeout(timerDoOuvir.current), []);

  if (!audioDisponivel()) return null;

  const ouvir = (tipo: TipoDeSom) => {
    tocarSomDeAviso(tipo, { noGesto: true });
    setTocando(tipo);
    // Dois "Ouvir" seguidos: o segundo reinicia a contagem, e o primeiro não apaga o destaque dele.
    window.clearTimeout(timerDoOuvir.current);
    timerDoOuvir.current = window.setTimeout(
      () => setTocando(null),
      tipo === "critico" ? 1000 : 500
    );
  };
  const trocar = (novo: boolean) => {
    definir(novo);
    if (novo) tocarSomDeAviso("aviso", { noGesto: true });
  };

  return (
    <Card className={className}>
      <CardContent className="flex items-start gap-3 p-4 sm:p-6">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
        >
          <Volume2 className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <label htmlFor={id} className="text-sm font-medium">
            Som neste aparelho
          </label>
          <p className="text-xs text-muted-foreground">
            Toca só neste navegador. Mesmo desligado, o &quot;Ouvir&quot; toca para você conferir.
          </p>
          {/* `flex-wrap`: a 390 px os dois botões (~245 px) não cabem ao lado do interruptor; no
              desktop cabem lado a lado e nada quebra. */}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px] sm:min-h-0"
              aria-pressed={tocando === "aviso"}
              onClick={() => ouvir("aviso")}
            >
              {tocando === "aviso" ? (
                <Volume2 className="mr-1 h-3 w-3" />
              ) : (
                <Play className="mr-1 h-3 w-3" />
              )}
              Ouvir o aviso
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px] sm:min-h-0"
              aria-pressed={tocando === "critico"}
              onClick={() => ouvir("critico")}
            >
              {tocando === "critico" ? (
                <Volume2 className="mr-1 h-3 w-3" />
              ) : (
                <Play className="mr-1 h-3 w-3" />
              )}
              Ouvir o crítico
            </Button>
          </div>
        </div>
        <Switch id={id} checked={ligado} onCheckedChange={trocar} className="shrink-0" />
      </CardContent>
    </Card>
  );
}
